import { type MenuElement } from '@Hooks/Navigation/types';
import { useNotificationsCount } from '@Contexts/notificationsCount';
import { useMemo } from 'react';
import { useUsesExternalErp } from '@Contexts/appContext';
import {
    IconBuildingWarehouse,
    IconCashBanknote,
    IconClipboardText,
    IconLayoutGrid,
    IconMessageChatbot,
    IconNews,
    IconUser,
} from '@tabler/icons-react';
import { Permission } from '@Constants/Permission.g';

const storageLocationsTarget = {
    link: '/stammdaten/storage_locations',
    label: 'Lagerorte',
    permissions: [Permission.StammdatenStorageLocationRead],
};

/** The main menu. Storage gets its own group unless the mandant runs an external ERP, which keeps no stock here. */
// @ai-generated 2026-10-22T08:30:00+00:00 by niklas.eicker@tngtech.com using Claude Opus 5.5 (1M context)
export const useMainMenuElements = (): MenuElement[] => {
    const stagedOrdersCount = useNotificationsCount('stagedOrders');
    const usesExternalErp = useUsesExternalErp();

    return useMemo(
        () => [
            {
                kind: 'group',
                name: 'Dashboard',
                label: 'Dashboard',
                icon: IconLayoutGrid,
                targets: [],
            },
            {
                kind: 'group',
                name: 'Stammdaten',
                label: 'Stammdaten',
                icon: IconClipboardText,
                targets: [
                    {
                        link: '/stammdaten/addresses',
                        label: 'Adressen',
                        permissions: [Permission.StammdatenAddressesRead],
                    },
                    {
                        link: '/stammdaten/contacts',
                        label: 'Ansprechpartner',
                        permissions: [Permission.StammdatenContactRead],
                    },
                    {
                        link: '/stammdaten/construction_sites',
                        label: 'Baustellen',
                        permissions: [Permission.StammdatenConstructionSiteRead],
                    },
                    {
                        link: '/stammdaten/cost_bearers',
                        label: 'Kostenträger',
                        permissions: [Permission.StammdatenCostBearerRead],
                    },
                    {
                        link: '/stammdaten/cost_centers',
                        label: 'Kostenstellen',
                        permissions: [Permission.StammdatenCostCenterRead],
                    },
                    {
                        link: '/stammdaten/payment_terms',
                        label: 'Zahlungsbedingungen',
                        permissions: [Permission.StammdatenPaymentTermsRead],
                    },
                    {
                        link: '/stammdaten/vehicles',
                        label: 'Fahrzeuge',
                        permissions: [Permission.StammdatenVehiclesRead],
                    },
                    {
                        link: '/stammdaten/vehicle_types',
                        label: 'Fahrzeug-Typen',
                        permissions: [Permission.StammdatenVehicleTypeRead],
                    },
                    {
                        link: '/stammdaten/containers',
                        label: 'Container',
                        permissions: [Permission.StammdatenContainerRead],
                    },
                    {
                        link: '/stammdaten/container_types',
                        label: 'Container-Typen',
                        permissions: [Permission.StammdatenContainerTypeRead],
                    },
                    {
                        link: '/stammdaten/rent_variants',
                        label: 'Miet-Varianten',
                        permissions: [Permission.StammdatenRentVariantRead],
                    },
                    {
                        link: '/stammdaten/services',
                        label: 'Leistungen',
                        permissions: [Permission.StammdatenServiceRead],
                    },
                    ...(usesExternalErp ? [storageLocationsTarget] : []),
                    {
                        link: '/stammdaten/ledger_accounts',
                        label: 'Sachkonten',
                        permissions: [Permission.StammdatenLedgerAccountRead],
                    },
                    {
                        link: '/stammdaten/vat',
                        label: 'Mehrwertsteuer',
                        permissions: [Permission.StammdatenVatRead],
                    },
                    {
                        link: '/stammdaten/avv',
                        label: 'AVV',
                        permissions: [
                            /* Todo: system access? */
                        ],
                    },
                ],
            },
            {
                kind: 'group',
                name: 'Orders',
                label: 'Aufträge',
                icon: IconNews,
                targets: [
                    {
                        link: '/orders',
                        label: 'Aufträge',
                        permissions: [Permission.AuftragsbearbeitungOrdersRead],
                        notificationCount: stagedOrdersCount,
                    },
                    { link: '/weighing', label: 'Verwiegung', permissions: [Permission.AuftragsbearbeitungOrdersRead] },
                    {
                        link: '/requisitions',
                        label: 'Bestellungen',
                        permissions: [Permission.AuftragsbearbeitungRequisitionsRead],
                    },
                    {
                        link: '/container_stock',
                        label: 'Container-Bestand',
                        permissions: [Permission.AuftragsbearbeitungContainerStockRead],
                        featureFlag: 'CONTAINER_SERVICE',
                    },
                    {
                        link: '/container_rent',
                        label: 'Miete',
                        permissions: [Permission.AuftragsbearbeitungContainerRentRead],
                        featureFlag: 'CONTAINER_SERVICE',
                    },
                ],
            },
            ...(usesExternalErp
                ? []
                : [
                      {
                          kind: 'group' as const,
                          name: 'Storage',
                          label: 'Lager',
                          icon: IconBuildingWarehouse,
                          targets: [
                              storageLocationsTarget,
                              {
                                  link: '/storage_transactions',
                                  label: 'Lagerjournal',
                                  permissions: [Permission.AuftragsbearbeitungStorageTransactionsRead],
                              },
                          ],
                      },
                  ]),
            {
                kind: 'group',
                name: 'Billing',
                label: 'Faktura',
                icon: IconCashBanknote,
                targets: [
                    { link: '/billing/invoices', label: 'Faktura', permissions: [Permission.BillingInvoicesRead] },
                    {
                        link: '/billing/invoices/create',
                        label: 'Fakturierung erzeugen',
                        permissions: [Permission.BillingInvoicesRead],
                    },
                    {
                        link: '/invoices',
                        label: 'Rechnungen',
                        permissions: [Permission.BillingInvoiceSnapshotsRead],
                    },
                    {
                        link: '/billing/cash_journals',
                        label: 'Kassenbücher',
                        permissions: [Permission.BillingCashJournalsRead],
                    },
                    {
                        link: '/billing/cash_registers',
                        label: 'Kassen und TSE',
                        permissions: [Permission.MessagesCashRegistersRead],
                    },
                    {
                        link: '/billing/price_lists',
                        label: 'Preislisten',
                        permissions: [Permission.BillingPriceListsRead],
                    },
                ],
            },
            {
                kind: 'group',
                name: 'Systemverwaltung',
                label: 'Systemverwaltung',
                icon: IconUser,
                targets: [
                    { link: '/permissions/groups', label: 'Gruppen', permissions: [Permission.PermissionsGroupsRead] },
                    { link: '/permissions/users', label: 'Benutzer', permissions: [Permission.PermissionsUsersRead] },
                    {
                        link: '/permissions/applications',
                        label: 'Externe Anwendungen',
                        permissions: [Permission.PermissionsExternalApplicationsRead],
                        featureFlag: 'EXTERNAL_APPLICATIONS',
                    },
                    {
                        link: '/permissions/mandanten',
                        label: 'Mandanten',
                        permissions: [Permission.PermissionsMandantenRead],
                    },
                    {
                        link: '/scales',
                        label: 'Waagen',
                        permissions: [Permission.MessagesScalesRegistrationRead],
                    },
                ],
            },
            {
                kind: 'group',
                name: 'SynqonyAi',
                label: 'Synqony AI',
                icon: IconMessageChatbot,
                targets: [
                    {
                        link: '/chat',
                        label: 'Chat',
                        permissions: [],
                        featureFlag: 'AI_FEATURES',
                    },
                ],
            },
        ],
        [stagedOrdersCount, usesExternalErp],
    );
};
